/**
 * Specs kit configurator: pills + live readout + scale bar + tab bar, driven by the JSON
 * payload `snippets/specs-payload.liquid` embeds next to the server-rendered panels.
 *
 * Pure helpers first (parsing/formatting/label/resolve/guard math — unit-tested by the
 * scratchpad Node harness), then the <specs-configurator> custom element that wires them to
 * the DOM. The module-level `if (module.exports)` guard at the bottom is inert in the
 * browser and lets the harness `require()` this file directly in Node.
 */
(function () {
  'use strict';

  var log = function () {
    if (window.Shopify && window.Shopify.designMode) {
      console.warn.apply(console, ['[specs-configurator]'].concat(Array.prototype.slice.call(arguments)));
    }
  };

  /* ------------------------------------------------------------------ *
   * Pure helpers
   * ------------------------------------------------------------------ */

  function round1(n) {
    return Math.round(n * 10) / 10;
  }

  function parseOunces(str) {
    if (!str) return 0;
    var lbMatch = /([\d.]+)\s*lb/i.exec(str);
    var ozMatch = /([\d.]+)\s*oz/i.exec(str);
    var lb = lbMatch ? parseFloat(lbMatch[1]) : 0;
    var oz = ozMatch ? parseFloat(ozMatch[1]) : 0;
    return round1(lb * 16 + oz);
  }

  // Matches how the tables themselves are written: "3 lb 2 oz", not "3 lb 2.0 oz".
  function formatOunces(totalOz) {
    var oz = round1(totalOz);
    var lb = Math.floor(oz / 16);
    var remOz = round1(oz - lb * 16);
    return lb + ' lb ' + remOz + ' oz';
  }

  function parseGrams(str) {
    if (!str) return 0;
    var m = /([\d.]+)\s*g/i.exec(str);
    return m ? parseFloat(m[1]) : 0;
  }

  function formatGrams(totalGrams) {
    return Math.round(totalGrams) + ' g';
  }

  function stripInsulated(str) {
    return (str || '').replace(/\s+insulated$/i, '').trim();
  }

  function flyLabel(raw, shortName) {
    if (!raw || raw === '-') return 'None';
    if (raw === 'Standard') return (shortName || 'Rainfly') + ' Rainfly';
    return raw;
  }

  function padLabel(raw, padsTable) {
    if (!raw || raw === '-') return 'None';
    var rawStripped = stripInsulated(raw).toLowerCase();
    var options = (padsTable && padsTable.options) || [];
    for (var i = 0; i < options.length; i++) {
      var nameStripped = stripInsulated(options[i].name);
      var nameLower = nameStripped.toLowerCase();
      var isMatch = rawStripped === nameLower || rawStripped.indexOf(nameLower) === 0 || nameLower.indexOf(rawStripped) === 0;
      if (nameLower && isMatch) return nameStripped + ' (' + options[i].rValue + ')';
    }
    return raw;
  }

  function comboLine(flyLabelStr, padLabelStr, resolved, note) {
    var hasRainflyWord = /rainfly/i.test(flyLabelStr);
    var flyPart = flyLabelStr === 'None' ? 'no rainfly' : hasRainflyWord ? flyLabelStr : flyLabelStr + ' rainfly';
    var padPart = padLabelStr === 'None' ? 'no pad' : padLabelStr + ' pad';
    var line = flyPart + ' + ' + padPart;
    if (!resolved) return line + ' — combination not offered';
    if (note) return line + ' — ' + note;
    return line;
  }

  function resolve(table, fly, pad) {
    var rows = (table && table.rows) || [];
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].fly === fly && rows[i].pad === pad) return rows[i];
    }
    return null;
  }

  // Cross-selection guard: `changedField` is whichever pill the user just clicked ('fly' or
  // 'pad'). If the current pair doesn't resolve, keep the changed field and fall back to the
  // first row that offers it, taking the other field from that row. Never returns undefined.
  function guard(table, fly, pad, changedField) {
    if (resolve(table, fly, pad)) return { fly: fly, pad: pad };
    var rows = (table && table.rows) || [];
    if (!rows.length) return { fly: fly, pad: pad };
    for (var i = 0; i < rows.length; i++) {
      if (changedField === 'pad' && rows[i].pad === pad) return { fly: rows[i].fly, pad: pad };
      if (changedField !== 'pad' && rows[i].fly === fly) return { fly: fly, pad: rows[i].pad };
    }
    return { fly: rows[0].fly, pad: rows[0].pad };
  }

  // The configurator offers a pad that no row lists, priced as "kit with no pad + this pad".
  // The pill carries the R-value ("True Level Spectre (R2.5)"); the table cell does not.
  function baseLabel(label) {
    return (label || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  }

  function addWeights(base, delta) {
    return {
      in: formatOunces(parseOunces(base.in) + parseOunces(delta.in)),
      cm: formatGrams(parseGrams(base.cm) + parseGrams(delta.cm))
    };
  }

  // Appends one derived row per rainfly that offers a no-pad row. Returns a new table; the
  // original is left alone so re-running this is safe.
  function withDerivedPad(table, derivedPad) {
    if (!table || !derivedPad || !derivedPad.enabled || !derivedPad.label) return table;
    var padValue = baseLabel(derivedPad.label);
    var rows = table.rows || [];
    var derivedRows = [];
    buildOrderedValues(rows, 'fly').forEach(function (fly) {
      var bare = resolve({ rows: rows }, fly, '-');
      if (!bare) return;
      derivedRows.push({
        fly: fly,
        pad: padValue,
        weight: addWeights(bare.weight, derivedPad.weight),
        derived: true
      });
    });
    if (!derivedRows.length) return table;
    return Object.assign({}, table, { rows: rows.concat(derivedRows) });
  }

  function cssAttrEscape(value) {
    return String(value == null ? '' : value).replace(/(["\\])/g, '\\$1');
  }

  // A weight cell carries both units as sibling spans; the unit toggle shows one of them.
  function buildCell(value, isWeight) {
    var td = document.createElement('td');
    td.className = 'specs-cfg__table-td' + (isWeight ? ' specs-cfg__table-td--value' : '');
    if (!isWeight) {
      td.textContent = value;
      return td;
    }
    ['in', 'cm'].forEach(function (unit) {
      var span = document.createElement('span');
      span.className = 'specs-cfg__unit-' + unit;
      span.textContent = value[unit];
      td.appendChild(span);
    });
    return td;
  }

  function scalePct(current, min, max) {
    if (max <= min) return 100;
    var pct = ((current - min) / (max - min)) * 100;
    return Math.max(3, Math.min(100, pct));
  }

  /* ------------------------------------------------------------------ *
   * <specs-configurator>
   * ------------------------------------------------------------------ */

  function buildOrderedValues(rows, key) {
    var seen = {};
    var values = [];
    rows.forEach(function (row) {
      if (!seen[row[key]]) {
        seen[row[key]] = true;
        values.push(row[key]);
      }
    });
    return values;
  }

  function tableExtremes(table, unit) {
    var parse = unit === 'cm' ? parseGrams : parseOunces;
    var values = table.rows.map(function (row) {
      return parse(row.weight[unit]);
    });
    return { min: Math.min.apply(Math, values), max: Math.max.apply(Math, values) };
  }

  if (typeof window !== 'undefined' && window.customElements && !customElements.get('specs-configurator')) {
    customElements.define(
      'specs-configurator',
      class SpecsConfigurator extends HTMLElement {
        connectedCallback() {
          var script = this.querySelector('[data-specs-payload]');
          if (!script) return log('no payload found');
          try {
            this.data = JSON.parse(script.textContent);
          } catch (err) {
            return log('payload did not parse', err);
          }

          var derivedPad = this.data.derivedPad;
          this.data.weightTables = (this.data.weightTables || []).map(function (table) {
            return withDerivedPad(table, derivedPad);
          });

          this.state = { modeIndex: 0, fly: null, pad: null, unit: 'in' };
          this.onDocClick = this.handleDocClick.bind(this);
          this.onDocKeydown = this.handleDocKeydown.bind(this);
          document.addEventListener('click', this.onDocClick);
          document.addEventListener('keydown', this.onDocKeydown);

          this.appendDerivedRowsToTables();
          this.resetSelectionToLastRow();
          this.wireUnitToggle();
          this.wireInfoTooltip();
          this.renderTabs();
          this.classList.add('is-enhanced');
          this.renderAll();
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocClick);
          document.removeEventListener('keydown', this.onDocKeydown);
        }

        get activeTable() {
          return this.data.weightTables[this.state.modeIndex] || null;
        }

        // The derived pad has no Pads-table column, and padLabel's prefix matching would
        // otherwise mistake "True Level Spectre" for the "True Level" column.
        padLabelFor(value) {
          var derivedPad = this.data.derivedPad;
          if (derivedPad && derivedPad.enabled && derivedPad.label && value === baseLabel(derivedPad.label)) {
            return derivedPad.label;
          }
          return padLabel(value, this.data.padsTable);
        }

        // The "All weights" tables are server-rendered from real rows only. Derived rows are
        // computed here, so without JS the tables simply show what the theme actually stores.
        appendDerivedRowsToTables() {
          this.data.weightTables.forEach((table) => {
            var derived = (table.rows || []).filter((row) => row.derived);
            if (!derived.length) return;
            var selector = '[data-specs-weight-table="' + cssAttrEscape(table.title) + '"]';
            var body = this.querySelector(selector + ' tbody');
            if (!body) return;
            derived.forEach((row) => body.appendChild(this.buildDerivedRow(row)));
          });
        }

        buildDerivedRow(row) {
          var tr = document.createElement('tr');
          tr.className = 'specs-cfg__table-row';
          tr.appendChild(buildCell(row.fly, false));
          tr.appendChild(buildCell(row.pad, false));
          tr.appendChild(buildCell(row.weight, true));
          return tr;
        }

        resetSelectionToLastRow() {
          var table = this.activeTable;
          if (!table || !table.rows.length) return;
          var lastRow = table.rows[table.rows.length - 1];
          this.state.fly = lastRow.fly;
          this.state.pad = lastRow.pad;
        }

        renderAll() {
          this.renderPills();
          this.renderReadout();
          this.renderScale();
          this.renderReadoutRows();
        }

        /* ---------------- pills ---------------- */

        renderPills() {
          var table = this.activeTable;
          if (!table) return;
          this.renderPillGroup('fly', buildOrderedValues(table.rows, 'fly'), this.state.fly, (value) =>
            flyLabel(value, this.data.shortName)
          );
          this.renderPillGroup('pad', buildOrderedValues(table.rows, 'pad'), this.state.pad, (value) =>
            this.padLabelFor(value)
          );
          this.renderModeGroup();
        }

        renderPillGroup(key, values, selected, labelFor, onPick) {
          var wrap = this.querySelector('[data-specs-pills="' + key + '"]');
          if (!wrap) return;
          var pick = onPick || ((value) => this.selectValue(key, value));
          wrap.innerHTML = '';
          values.forEach((value, index) => {
            var button = document.createElement('button');
            button.type = 'button';
            button.className = 'specs-cfg__pill';
            button.textContent = labelFor(value);
            button.setAttribute('aria-pressed', String(value === selected));
            button.addEventListener('click', () => pick(value, index));
            wrap.appendChild(button);
          });
        }

        renderModeGroup() {
          var group = this.querySelector('[data-specs-group="mode"]');
          var tables = this.data.weightTables;
          if (!group) return;
          group.hidden = tables.length < 2;
          if (tables.length < 2) return;
          var titles = tables.map((t) => t.title);
          this.renderPillGroup(
            'mode',
            titles,
            tables[this.state.modeIndex].title,
            (value) => value,
            (value, index) => this.selectMode(index)
          );
        }

        selectValue(key, value) {
          var table = this.activeTable;
          var next =
            key === 'fly'
              ? guard(table, value, this.state.pad, 'fly')
              : guard(table, this.state.fly, value, 'pad');
          this.state.fly = next.fly;
          this.state.pad = next.pad;
          this.renderAll();
        }

        selectMode(index) {
          this.state.modeIndex = index;
          this.resetSelectionToLastRow();
          this.renderAll();
        }

        /* ---------------- readout ---------------- */

        renderReadout() {
          var table = this.activeTable;
          if (!table) return;
          var row = resolve(table, this.state.fly, this.state.pad);
          var unit = this.state.unit;
          var eyebrow = this.querySelector('[data-specs-eyebrow]');
          if (eyebrow) eyebrow.textContent = table.title;

          var valueEl = this.querySelector('[data-specs-weight-value]');
          if (valueEl) valueEl.textContent = row ? row.weight[unit] : '—';

          var fLabel = flyLabel(this.state.fly, this.data.shortName);
          var pLabel = this.padLabelFor(this.state.pad);
          var note = row && row.derived ? (this.data.derivedPad || {}).note || '' : '';
          var comboEl = this.querySelector('[data-specs-combo]');
          if (comboEl) comboEl.textContent = comboLine(fLabel, pLabel, !!row, note);
        }

        renderScale() {
          var table = this.activeTable;
          if (!table) return;
          var unit = this.state.unit;
          var parse = unit === 'cm' ? parseGrams : parseOunces;
          var format = unit === 'cm' ? formatGrams : formatOunces;
          var extremes = tableExtremes(table, unit);
          var row = resolve(table, this.state.fly, this.state.pad);
          var current = row ? parse(row.weight[unit]) : extremes.min;

          var fill = this.querySelector('[data-specs-scale-fill]');
          if (fill) fill.style.width = scalePct(current, extremes.min, extremes.max) + '%';

          var minEl = this.querySelector('[data-specs-scale-min]');
          var maxEl = this.querySelector('[data-specs-scale-max]');
          if (minEl) minEl.textContent = 'Lightest ' + format(extremes.min);
          if (maxEl) maxEl.textContent = 'Heaviest ' + format(extremes.max);
        }

        renderReadoutRows() {
          this.renderReadoutRow('packed-size', '[data-specs-packed-size]', this.data.packedSize);
          this.renderReadoutRow('capacity', '[data-specs-capacity]', this.data.capacity);
        }

        renderReadoutRow(rowKey, valueSelector, value) {
          var row = this.querySelector('[data-specs-row="' + rowKey + '"]');
          var valueEl = this.querySelector(valueSelector);
          var text = value ? value[this.state.unit] : '';
          if (valueEl) valueEl.textContent = text;
          if (row) row.hidden = !text;
        }

        /* ---------------- unit toggle ---------------- */

        wireUnitToggle() {
          var buttons = this.querySelectorAll('[data-specs-unit]');
          buttons.forEach((button) => {
            button.setAttribute('aria-pressed', String(button.dataset.specsUnit === this.state.unit));
            button.addEventListener('click', () => {
              this.state.unit = button.dataset.specsUnit;
              this.classList.toggle('is-cm', this.state.unit === 'cm');
              buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
              this.renderScale();
              this.renderReadoutRows();
              var valueEl = this.querySelector('[data-specs-weight-value]');
              var row = resolve(this.activeTable, this.state.fly, this.state.pad);
              if (valueEl) valueEl.textContent = row ? row.weight[this.state.unit] : '—';
            });
          });
        }

        /* ---------------- info tooltip ---------------- */

        wireInfoTooltip() {
          this.infoToggle = this.querySelector('[data-specs-info-toggle]');
          this.tooltip = this.querySelector('[data-specs-tooltip]');
          if (!this.infoToggle || !this.tooltip) return;
          this.infoToggle.addEventListener('click', () => this.toggleTooltip());
          this.infoToggle.addEventListener('focus', () => this.openTooltip());
          this.infoToggle.addEventListener('blur', () => this.closeTooltip());
          this.infoToggle.addEventListener('mouseenter', () => this.openTooltip());
          this.infoToggle.addEventListener('mouseleave', () => this.closeTooltip());
          this.tooltip.addEventListener('mouseenter', () => this.openTooltip());
          this.tooltip.addEventListener('mouseleave', () => this.closeTooltip());
        }

        openTooltip() {
          this.tooltip.hidden = false;
          this.infoToggle.setAttribute('aria-expanded', 'true');
        }

        closeTooltip() {
          this.tooltip.hidden = true;
          this.infoToggle.setAttribute('aria-expanded', 'false');
        }

        toggleTooltip() {
          if (this.tooltip.hidden) this.openTooltip();
          else this.closeTooltip();
        }

        handleDocClick(event) {
          if (!this.tooltip || this.tooltip.hidden) return;
          if (this.tooltip.contains(event.target) || event.target === this.infoToggle) return;
          this.closeTooltip();
        }

        handleDocKeydown(event) {
          if (event.key === 'Escape' && this.tooltip && !this.tooltip.hidden) this.closeTooltip();
          if (this.tabButtons) this.handleTabKeydown(event);
        }

        /* ---------------- tabs ---------------- */

        renderTabs() {
          this.tabButtons = Array.prototype.slice.call(this.querySelectorAll('[data-specs-tab]'));
          var tabbar = this.querySelector('[data-specs-tabbar]');
          if (tabbar) tabbar.setAttribute('role', 'tablist');
          this.tabButtons.forEach((button, i) => this.setupTabButton(button, i));
          this.activateTab(0);
        }

        setupTabButton(button, index) {
          var key = button.dataset.specsTab;
          var panel = this.querySelector('[data-specs-panel="' + key + '"]');
          if (!panel) return;
          var tabId = 'specs-cfg-tab-' + key;
          var panelId = 'specs-cfg-panel-' + key;
          button.id = tabId;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-controls', panelId);
          button.setAttribute('tabindex', index === 0 ? '0' : '-1');
          panel.id = panelId;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tabId);
          panel.setAttribute('tabindex', '0');
          button.addEventListener('click', () => this.activateTab(this.tabButtons.indexOf(button)));
        }

        activateTab(index) {
          this.tabButtons.forEach((button, i) => {
            var key = button.dataset.specsTab;
            var panel = this.querySelector('[data-specs-panel="' + key + '"]');
            var isActive = i === index;
            button.setAttribute('aria-selected', String(isActive));
            button.setAttribute('tabindex', isActive ? '0' : '-1');
            button.classList.toggle('is-active', isActive);
            if (panel) panel.classList.toggle('is-active', isActive);
          });
        }

        handleTabKeydown(event) {
          var current = this.tabButtons.indexOf(document.activeElement);
          if (current === -1) return;
          var next = this.tabKeydownTarget(event, current);
          if (next === null) return;
          event.preventDefault();
          this.activateTab(next);
          this.tabButtons[next].focus();
        }

        tabKeydownTarget(event, current) {
          var count = this.tabButtons.length;
          if (event.key === 'ArrowRight') return (current + 1) % count;
          if (event.key === 'ArrowLeft') return (current - 1 + count) % count;
          if (event.key === 'Home') return 0;
          if (event.key === 'End') return count - 1;
          return null;
        }
      }
    );
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      parseOunces: parseOunces,
      formatOunces: formatOunces,
      parseGrams: parseGrams,
      formatGrams: formatGrams,
      stripInsulated: stripInsulated,
      flyLabel: flyLabel,
      padLabel: padLabel,
      comboLine: comboLine,
      resolve: resolve,
      guard: guard,
      scalePct: scalePct,
      baseLabel: baseLabel,
      addWeights: addWeights,
      withDerivedPad: withDerivedPad,
    };
  }
})();
